import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ITEMS = [
  {
    id: 9,
    title: "Smart Classroom",
    image: "/images/services/p1.png",
    modalImages: ["/images/services/paket1.png"],
    description:
      "Rangkaian perangkat presentasi & kolaborasi interaktif untuk pembelajaran modern.",
    slug: "classroom",
  },
  {
    id: 10,
    title: "Smart Meeting Room",
    image: "/images/services/p2.png",
    modalImages: ["/images/services/paket2.png"],
    description:
      "VC, audio processing, dan kontrol terpadu agar meeting efisien & bebas ribet.",
    slug: "meetingroom",
  },
  {
    id: 11,
    title: "Smart Auditorium",
    image: "/images/services/p3.png",
    modalImages: ["/images/services/paket3.png"],
    description:
      "Sistem pro-AV skala besar: routing, scaling, dan multi-source display yang stabil.",
    slug: "auditorium",
  },
];

export default function ProductService() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const active = useMemo(() => ITEMS[activeIndex] ?? null, [activeIndex]);

  const openModal = useCallback((idx) => {
    setActiveIndex(idx);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsOpen(false), []);

  /* Lock scroll */
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  /* ESC handler */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && closeModal();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeModal]);

  const closeBtnRef = useRef(null);
  useEffect(() => {
    if (isOpen) closeBtnRef.current?.focus();
  }, [isOpen]);

  return (
    <>
      <div
        id="solutions"
        className="w-full max-w-2xl mx-auto mb-8 px-4 text-center"
      >
        <h2 className="text-[22px] sm:text-[24px] md:text-[35px] text-[#2E7462] mb-3 font-semibold">
          Solusi Kami
        </h2>
        <p className="text-[14px] sm:text-[15px] md:text-[16px] text-[#4F5665] leading-7">
          Temukan berbagai layanan dan produk unggulan kami, yang dirancang untuk
          memenuhi semua kebutuhan Anda.
        </p>
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {ITEMS.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openModal(i)}
              aria-label={`Lihat ${item.title}`}
              className="
                group relative rounded-xl overflow-hidden
                border bg-white shadow-md
                touch-manipulation active:scale-[0.97]
                transition-all hover:shadow-xl
                focus:outline-none focus:ring-2 focus:ring-[#2E7462]
              "
              data-aos="fade-up"
              data-aos-duration="800"
              data-aos-delay={i * 120}
            >
              <div className="relative w-full aspect-[4/3]">
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
              </div>

              <div className="pointer-events-none absolute inset-0 flex items-end">
                <div className="w-full p-4 sm:p-5">
                  <h3 className="text-white text-[16px] sm:text-lg md:text-xl font-semibold drop-shadow">
                    {item.title}
                  </h3>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {isOpen && active && (
        <div
          className="fixed inset-0 z-[999]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lb-title"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeModal}
          />

          <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4">
            <div
              className="
                relative bg-white rounded-xl sm:rounded-2xl
                w-full h-full sm:w-auto sm:h-auto
                max-w-[100vw] max-h-[100vh]
                sm:max-w-[95vw] sm:max-h-[95vh]
                flex items-center justify-center
              "
            >
              <button
                ref={closeBtnRef}
                type="button"
                onClick={closeModal}
                aria-label="Tutup"
                className="
                  absolute top-3 right-3 z-10
                  h-10 w-10 rounded-full
                  bg-black/70 text-white
                  flex items-center justify-center
                  hover:bg-black/80
                  focus:outline-none focus:ring-2 focus:ring-white
                "
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <figure className="flex items-center justify-center w-full h-full p-4">
                <img
                  src={active.modalImages?.[0] || active.image}
                  alt={active.title}
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
